import * as React from 'react';
import { camelCase } from 'lodash';

import FormField from 'components/form/form-field/FormField';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ICONS,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO
} from 'utils/deliverables';

import './deliverable-checkbox-filters.scss';

import type {
  AdminDeliverablesQuery,
  AdminDeliverablesSelectionQuery,
  DeliverableType,
  LocationWindow,
  TextEntry
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./deliverable-checkbox-filters.i18n.json'));

type Props = {
  location: LocationWindow,
  campaignDeliverableTypes?: Array<DeliverableType>,
  getSelectionFromQuery: (a?: AdminDeliverablesQuery | null) => AdminDeliverablesSelectionQuery,
  onFilterChange: (a: SyntheticEvent<TextEntry>) => void,
  onBooleanFilterChange: (a: SyntheticEvent<TextEntry>) => void
};

export const SUPPORTED_TYPE_OPTIONS = [DELIVERABLE_TYPE_INSTAGRAM_POST, DELIVERABLE_TYPE_INSTAGRAM_STORY, DELIVERABLE_TYPE_INSTAGRAM_REEL, DELIVERABLE_TYPE_TIKTOK_VIDEO];
const STATUS_OPTIONS = ['complete', 'incomplete', 'missing'];
const msgKey = 'deliverable_checkbox_filters';

class DeliverableCheckboxFilters extends React.PureComponent<Props> {
  getSelectionStatus(): AdminDeliverablesSelectionQuery {
    const { location, getSelectionFromQuery } = this.props;

    return getSelectionFromQuery(location.query);
  }

  renderTaskTypes(): React.ReactNode {
    const { campaignDeliverableTypes, onFilterChange } = this.props;
    const selection = this.getSelectionStatus();
    const availableDeliverableTypes = campaignDeliverableTypes?.length ?
      SUPPORTED_TYPE_OPTIONS.filter(type => campaignDeliverableTypes.includes(type)) : SUPPORTED_TYPE_OPTIONS;

    return (
      <FormField
        direction="column"
        inline
        showRequiredMark={false}
        showValidationIcon={false}
        label={__t(`${msgKey}.labels.task_type`)}
        labelType="highlighted"
        fontSize="small"
      >
        <div className="deliverable-checkbox-filters__section">
          {
            availableDeliverableTypes.map(type => (
              <div
                key={`deliverable-checkbox-filters--task-type-${type}`}
                className="deliverable-checkbox-filters__item"
              >
                <InputCheckbox
                  name="type"
                  value={type}
                  id={`deliverable-checkbox-filters--task-type-${type}`}
                  size="small"
                  checked={selection.type.includes(type)}
                  onChange={onFilterChange}
                />
                <div className="deliverable-checkbox-filters__item-icon">
                  <Tooltip
                    message={__t(`${msgKey}.labels.task_types.${type}`)}
                    width="short"
                  >
                    <SVGDynamicIcon
                      name={`${DELIVERABLE_ICONS[camelCase(type)]}`}
                      width={16}
                      height={16}
                      customColor="black"
                    />
                  </Tooltip>
                </div>
              </div>
            ))
          }
        </div>
      </FormField>
    );
  }

  renderStatusOptions(): React.ReactNode {
    const { onFilterChange } = this.props;
    const selection = this.getSelectionStatus();

    return (
      <FormField
        direction="column"
        inline
        showRequiredMark={false}
        showValidationIcon={false}
        label={__t(`${msgKey}.labels.analytics_upload_status`)}
        labelType="highlighted"
        fontSize="small"
      >
        <div className="deliverable-checkbox-filters__section">
          {
            STATUS_OPTIONS.map(status => (
              <div
                key={`deliverable-checkbox-filters--status-type-${status}`}
                className="deliverable-checkbox-filters__item"
              >
                <InputCheckbox
                  key={`deliverable-checkbox-filters--status-${status}`}
                  id={`deliverable-checkbox-filters--status-${status}`}
                  name="status"
                  value={status}
                  checked={selection.status.includes(status)}
                  label={__t(`${msgKey}.labels.statuses.${status}`)}
                  onChange={onFilterChange}
                />
              </div>
            ))
          }
        </div>
      </FormField>
    );
  }

  renderOther(): React.ReactNode {
    const { onBooleanFilterChange } = this.props;
    const selection = this.getSelectionStatus();

    return (
      <FormField
        direction="column"
        inline
        showRequiredMark={false}
        showValidationIcon={false}
        label={__t(`${msgKey}.labels.other`)}
        labelType="highlighted"
        fontSize="small"
      >
        <div className="deliverable-checkbox-filters__section">
          <div className="deliverable-checkbox-filters__item">
            <InputCheckbox
              name="mismatchedAnalytics"
              id="deliverable-checkbox-filters--other-mismatched"
              checked={selection.mismatchedAnalytics}
              label={__t(`${msgKey}.labels.mismatched_analytics`)}
              onChange={onBooleanFilterChange}
            />
          </div>
        </div>
      </FormField>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="deliverable-checkbox-filters">
        {this.renderTaskTypes()}
        {this.renderStatusOptions()}
        {this.renderOther()}
      </div>
    );
  }
}

export default DeliverableCheckboxFilters;
